import {
  Team,
  ClassicLadderMatch,
  ClassicLadderRound,
  ClassicLadderTournament,
  GameResult,
} from "../types/tournament";
import { generateId, createGameResult } from "./tournamentLogic";

// Create a new Classic Speed Ladder tournament
export const createClassicLadderTournament = (
  name: string,
  teams: Team[],
  courts: number,
  totalRounds: number
): ClassicLadderTournament => {
  // Shuffle teams randomly for initial placement
  const shuffledTeams = [...teams].sort(() => Math.random() - 0.5);

  // Assign initial court positions
  const teamsWithCourts = shuffledTeams.map((team, index) => ({
    ...team,
    currentCourt: Math.floor(index / 2) + 1,
    startingCourt: Math.floor(index / 2) + 1,
  }));

  const initialRound = createClassicLadderRound(teamsWithCourts, 1);

  // Generate court configurations
  const courtConfigs = Array.from({ length: courts }, (_, i) => ({
    id: `court-${i + 1}`,
    name: `Court ${i + 1}`,
    isActive: true,
  }));

  return {
    id: generateId(),
    name,
    teams: teamsWithCourts,
    rounds: [initialRound],
    currentRound: 1,
    totalRounds,
    courts: courtConfigs,
    playerConfig: {
      playersPerTeam: 2,
      minTeamsRequired: 4,
      maxTeamsAllowed: courts * 2 + 2,
      totalPlayersExpected: teams.length * 2,
      allowSubstitutes: false,
      maxSubstitutesPerTeam: 0,
    },
    isActive: true,
    createdAt: new Date(),
    updatedAt: new Date(),
    liveDashboardEnabled: true,
    qrCodeUrl: "",
    tournamentType: "classic_ladder",
    champion: null,
    court1Defender: null,
    court1DefenderStreak: 0,
  };
};

// Create a round for Classic Speed Ladder
export const createClassicLadderRound = (
  teams: Team[],
  roundNumber: number
): ClassicLadderRound => {
  // Group teams by court
  const teamsByCourt: { [court: number]: Team[] } = {};
  teams.forEach((team) => {
    if (!teamsByCourt[team.currentCourt]) {
      teamsByCourt[team.currentCourt] = [];
    }
    teamsByCourt[team.currentCourt].push(team);
  });

  const matches: ClassicLadderMatch[] = [];

  // Create matches for each court
  Object.keys(teamsByCourt).forEach((courtStr) => {
    const court = parseInt(courtStr);
    const courtTeams = teamsByCourt[court];

    if (courtTeams.length >= 2) {
      // If we have exactly 2 teams, create a match
      if (courtTeams.length === 2) {
        const match: ClassicLadderMatch = {
          id: generateId(),
          round: roundNumber,
          court,
          team1: courtTeams[0],
          team2: courtTeams[1],
          result: null,
          isCompleted: false,
        };
        matches.push(match);
      } else if (courtTeams.length > 2) {
        // If we have more than 2 teams on a court, pair them up
        for (let i = 0; i < courtTeams.length - 1; i += 2) {
          if (i + 1 < courtTeams.length) {
            const match: ClassicLadderMatch = {
              id: generateId(),
              round: roundNumber,
              court,
              team1: courtTeams[i],
              team2: courtTeams[i + 1],
              result: null,
              isCompleted: false,
            };
            matches.push(match);
          }
        }
      }
    }
  });

  return {
    roundNumber,
    matches,
    isCompleted: false,
  };
};

// Update match result and team positions
export const updateClassicLadderMatch = (
  tournament: ClassicLadderTournament,
  matchId: string,
  team1Score: number,
  team2Score: number
): ClassicLadderTournament => {
  const result = createGameResult(team1Score, team2Score, "", "");

  // Find the match
  let targetMatch: ClassicLadderMatch | null = null;
  let targetRoundIndex = -1;
  let targetMatchIndex = -1;

  for (
    let roundIndex = 0;
    roundIndex < tournament.rounds.length;
    roundIndex++
  ) {
    const round = tournament.rounds[roundIndex];
    for (let matchIndex = 0; matchIndex < round.matches.length; matchIndex++) {
      const match = round.matches[matchIndex];
      if (match.id === matchId) {
        targetMatch = match;
        targetRoundIndex = roundIndex;
        targetMatchIndex = matchIndex;
        break;
      }
    }
    if (targetMatch) break;
  }

  if (!targetMatch) return tournament;

  // Update match result
  const updatedMatch: ClassicLadderMatch = {
    ...targetMatch,
    result,
    isCompleted: true,
  };

  // Update teams stats and court positions
  const winnerId = result.winner;
  const loserId = result.loser;

  const updatedTeams = tournament.teams.map((team) => {
    if (team.id === winnerId) {
      const newCourt = Math.max(1, team.currentCourt - 1); // Winner moves UP (lower court number)
      return {
        ...team,
        wins: team.wins + 1,
        currentCourt: newCourt,
        totalMovement: team.totalMovement + (team.currentCourt - newCourt),
      };
    }
    if (team.id === loserId) {
      const maxCourt = tournament.courts.length;
      const newCourt = Math.min(maxCourt, team.currentCourt + 1); // Loser moves DOWN (higher court number)
      return {
        ...team,
        losses: team.losses + 1,
        currentCourt: newCourt,
        totalMovement:
          team.totalMovement + Math.abs(team.currentCourt - newCourt),
      };
    }
    return team;
  });

  // Update rounds
  const updatedRounds = [...tournament.rounds];
  updatedRounds[targetRoundIndex] = {
    ...updatedRounds[targetRoundIndex],
    matches: updatedRounds[targetRoundIndex].matches.map((match, index) =>
      index === targetMatchIndex ? updatedMatch : match
    ),
  };

  // Check if round is complete
  const currentRound = updatedRounds[targetRoundIndex];
  const isRoundComplete = currentRound.matches.every(
    (match) => match.isCompleted
  );

  if (isRoundComplete) {
    updatedRounds[targetRoundIndex] = {
      ...currentRound,
      isCompleted: true,
    };
  }

  // Update Court 1 defender tracking
  const court1Team = updatedTeams.find((team) => team.currentCourt === 1);
  let court1DefenderStreak = tournament.court1DefenderStreak;
  let court1Defender = tournament.court1Defender;

  if (court1Team && tournament.court1Defender?.id === court1Team.id) {
    // Same team still on Court 1
    court1DefenderStreak += 1;
  } else if (court1Team) {
    // New team on Court 1
    court1Defender = court1Team;
    court1DefenderStreak = 1;
  }

  // Check if tournament is complete
  const isTournamentComplete =
    tournament.currentRound === tournament.totalRounds && isRoundComplete;
  const champion = isTournamentComplete
    ? court1Team || null
    : tournament.champion;

  return {
    ...tournament,
    teams: updatedTeams,
    rounds: updatedRounds,
    champion,
    court1Defender,
    court1DefenderStreak,
    updatedAt: new Date(),
  };
};

// Create next round
export const createNextClassicLadderRound = (
  tournament: ClassicLadderTournament
): ClassicLadderTournament => {
  if (tournament.currentRound >= tournament.totalRounds) {
    return tournament;
  }

  const nextRoundNumber = tournament.currentRound + 1;
  const nextRound = createClassicLadderRound(tournament.teams, nextRoundNumber);

  return {
    ...tournament,
    rounds: [...tournament.rounds, nextRound],
    currentRound: nextRoundNumber,
    updatedAt: new Date(),
  };
};

// Get current active matches
export const getCurrentClassicLadderMatches = (
  tournament: ClassicLadderTournament
): ClassicLadderMatch[] => {
  const currentRound = tournament.rounds.find(
    (round) => round.roundNumber === tournament.currentRound
  );
  return currentRound?.matches.filter((match) => !match.isCompleted) || [];
};

// Get tournament progress
export const getClassicLadderProgress = (
  tournament: ClassicLadderTournament
) => {
  const totalMatches = tournament.rounds.reduce(
    (total, round) => total + round.matches.length,
    0
  );
  const completedMatches = tournament.rounds.reduce(
    (total, round) =>
      total + round.matches.filter((match) => match.isCompleted).length,
    0
  );

  return {
    totalMatches,
    completedMatches,
    progress: totalMatches > 0 ? (completedMatches / totalMatches) * 100 : 0,
    roundsCompleted: tournament.rounds.filter((round) => round.isCompleted)
      .length,
  };
};

// Get ladder standings (teams sorted by current court)
export const getLadderStandings = (
  tournament: ClassicLadderTournament
): Team[] => {
  return [...tournament.teams].sort((a, b) => {
    // Sort by current court (ascending), then by wins (descending)
    if (a.currentCourt === b.currentCourt) {
      return b.wins - a.wins;
    }
    return a.currentCourt - b.currentCourt;
  });
};

// Get Court 1 battle history
export const getCourt1History = (
  tournament: ClassicLadderTournament
): Team[] => {
  const court1Teams: Team[] = [];

  tournament.rounds.forEach((round) => {
    round.matches.forEach((match) => {
      if (match.court === 1 && match.isCompleted && match.result) {
        const winnerId = match.result.winner;
        const winnerTeam =
          match.team1.id === winnerId ? match.team1 : match.team2;
        if (!court1Teams.find((team) => team.id === winnerTeam.id)) {
          court1Teams.push(winnerTeam);
        }
      }
    });
  });

  return court1Teams;
};

// Calculate tournament statistics specific to Classic Ladder
export const calculateClassicLadderStats = (
  tournament: ClassicLadderTournament
) => {
  const teams = tournament.teams;
  const mostWins = teams.reduce((prev, current) =>
    current.wins > prev.wins ? current : prev
  );

  const biggestClimber = teams.reduce((prev, current) => {
    const currentClimb = current.startingCourt - current.currentCourt;
    const prevClimb = prev.startingCourt - prev.currentCourt;
    return currentClimb > prevClimb ? current : prev;
  });

  const mostActive = teams.reduce((prev, current) =>
    current.totalMovement > prev.totalMovement ? current : prev
  );

  const court1History = getCourt1History(tournament);

  return {
    mostWins,
    biggestClimber,
    mostActive,
    court1History,
    court1Defender: tournament.court1Defender,
    court1DefenderStreak: tournament.court1DefenderStreak,
    totalGamesPlayed:
      teams.reduce((total, team) => total + team.wins + team.losses, 0) / 2,
    averageMovement:
      teams.reduce((total, team) => total + team.totalMovement, 0) /
      teams.length,
  };
};
